import React, { useState } from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Chip, 
  IconButton, 
  Menu, 
  MenuItem, 
  ListItemIcon, 
  ListItemText,
  Tooltip,
  Divider,
  useTheme
} from '@mui/material';
import { 
  MoreVert, 
  Edit, 
  Delete, 
  CheckCircle, 
  RadioButtonUnchecked,
  AccessTime,
  Flag,
  AutoAwesome,
  Visibility
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { Task } from '../../types/task';

interface TaskCardProps {
  task: Task;
  onEdit: (task: Task) => void;
  onDelete: (id: number) => void;
  onStatusChange: (id: number, completed: boolean) => void;
  onSummarize: (description: string) => void;
  onViewDetails?: (task: Task) => void;
}

const TaskCard: React.FC<TaskCardProps> = ({ 
  task, 
  onEdit, 
  onDelete, 
  onStatusChange,
  onSummarize,
  onViewDetails
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  
  // Handle menu button click
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation(); // Prevent card click
    event.preventDefault(); // Prevent default behavior
    setAnchorEl(event.currentTarget);
  };
  
  // Handle menu close
  const handleMenuClose = (event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation(); // Prevent card click
    }
    setAnchorEl(null);
  };

  // Handle status change (checkbox click)
  const handleStatusChange = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card click
    event.preventDefault(); // Prevent default behavior
    onStatusChange(task.id, !task.completed);
  };

  // Handle card click
  const handleCardClick = () => {
    if (onViewDetails) {
      onViewDetails(task);
    }
  };

  // Handle menu item clicks
  const handleEdit = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card click
    handleMenuClose();
    onEdit(task);
  };

  const handleDelete = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card click
    handleMenuClose();
    onDelete(task.id);
  };

  const handleSummarize = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card click
    handleMenuClose();
    onSummarize(task.description);
  };

  const handleViewDetails = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card click
    handleMenuClose();
    if (onViewDetails) {
      onViewDetails(task);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'high':
        return theme.palette.error.main;
      case 'medium':
        return theme.palette.warning.main;
      case 'low':
        return theme.palette.success.main;
      default:
        return theme.palette.info.main;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return theme.palette.success.main;
      case 'in-progress':
        return theme.palette.warning.main;
      case 'todo':
        return theme.palette.info.main;
      default:
        return theme.palette.info.main;
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'No due date';
    try {
      const date = new Date(dateString);
      const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
      return date.toLocaleDateString('en-US', options);
    } catch (e) {
      return 'Invalid date';
    }
  };

  // Check if task has subtasks
  const hasSubtasks = task.description && task.description.includes('## Subtasks:');

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      whileHover={{ y: -5 }}
      style={{ height: '100%' }}
    >
      <Card 
        onClick={handleCardClick}
        sx={{ 
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden',
          background: 'rgba(30, 30, 30, 0.8)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          borderLeft: `4px solid ${getPriorityColor(task.priority)}`,
          transition: 'all 0.3s ease',
          cursor: onViewDetails ? 'pointer' : 'default',
          '&:hover': {
            boxShadow: `0 12px 28px rgba(0, 0, 0, 0.4), 0 0 10px ${getPriorityColor(task.priority)}40`,
          }
        }}
      >
        <CardContent sx={{ 
          p: 3, 
          height: '100%',
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'space-between'
        }}>
          {/* Header section with fixed height */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'flex-start'
            }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                width: 'calc(100% - 40px)' // Reserve space for menu button
              }}>
                <IconButton 
                  size="small" 
                  onClick={handleStatusChange}
                  sx={{ 
                    color: task.completed ? theme.palette.success.main : theme.palette.text.secondary,
                    flexShrink: 0,
                    '&:hover': {
                      backgroundColor: task.completed 
                        ? `${theme.palette.success.main}20` 
                        : `${theme.palette.primary.main}20`,
                    }
                  }}
                >
                  {task.completed ? <CheckCircle /> : <RadioButtonUnchecked />}
                </IconButton>
                <Typography 
                  variant="h6" 
                  component="h2" 
                  sx={{ 
                    fontWeight: 600,
                    textDecoration: task.completed ? 'line-through' : 'none',
                    color: task.completed ? 'text.secondary' : 'text.primary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    lineHeight: 1.3,
                    minHeight: '2.6em', // Ensure consistent height for title
                  }}
                >
                  {task.title}
                </Typography>
              </Box>
              
              <IconButton
                aria-label="more"
                id={`task-menu-button-${task.id}`}
                aria-controls={open ? `task-menu-${task.id}` : undefined}
                aria-expanded={open ? 'true' : undefined}
                aria-haspopup="true"
                onClick={handleMenuClick}
                sx={{ 
                  ml: 1,
                  flexShrink: 0,
                  '&:hover': {
                    backgroundColor: `${theme.palette.primary.main}20`,
                  }
                }}
              >
                <MoreVert />
              </IconButton>
            </Box>
          </Box>
          
          {/* Content section with fixed height */}
          <Box sx={{ 
            flexGrow: 1, 
            display: 'flex', 
            flexDirection: 'column',
            minHeight: '80px', // Ensure minimum height for description
            maxHeight: '80px', // Ensure maximum height for description
            mb: 2
          }}>
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                lineHeight: 1.5,
              }}
            >
              {task.description ? (
                hasSubtasks ? (
                  <>
                    {task.description.split('## Subtasks:')[0].trim() || 'Task with subtasks'}
                    <Box component="span" sx={{ 
                      color: theme.palette.primary.main, 
                      display: 'block',
                      mt: 0.5,
                      fontSize: '0.9em'
                    }}>
                      • Has subtasks (click to view)
                    </Box>
                  </>
                ) : task.description
              ) : 'No description provided'}
            </Typography>
          </Box>
          
          {/* Footer section with fixed height */}
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 1, 
            mt: 'auto',
            height: '32px', // Fixed height for the footer
          }}>
            <Tooltip title="Due date">
              <Chip 
                icon={<AccessTime fontSize="small" />} 
                label={formatDate(task.due_date)} 
                size="small"
                sx={{ 
                  backgroundColor: 'rgba(0, 0, 0, 0.2)',
                  backdropFilter: 'blur(5px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  height: '24px',
                }}
              />
            </Tooltip>
            
            <Tooltip title="Priority">
              <Chip 
                icon={<Flag fontSize="small" />} 
                label={task.priority} 
                size="small"
                sx={{ 
                  backgroundColor: `${getPriorityColor(task.priority)}20`,
                  color: getPriorityColor(task.priority),
                  border: `1px solid ${getPriorityColor(task.priority)}50`,
                  height: '24px',
                }}
              />
            </Tooltip>
            
            <Tooltip title="Status">
              <Chip 
                label={task.status} 
                size="small"
                sx={{ 
                  backgroundColor: `${getStatusColor(task.status)}20`,
                  color: getStatusColor(task.status),
                  border: `1px solid ${getStatusColor(task.status)}50`,
                  height: '24px',
                }}
              />
            </Tooltip>
          </Box>
        </CardContent>
      </Card>
      
      {/* Menu positioned outside the card for better z-index handling */}
      <Menu
        id={`task-menu-${task.id}`}
        anchorEl={anchorEl}
        open={open}
        onClose={() => handleMenuClose()}
        MenuListProps={{
          'aria-labelledby': `task-menu-button-${task.id}`,
        }}
        PaperProps={{
          sx: {
            mt: 1.5,
            backdropFilter: 'blur(10px)',
            backgroundColor: 'rgba(30, 30, 30, 0.9)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.5)',
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        disablePortal={false} // Use portal for better positioning
        disableScrollLock={false} // Allow scrolling when menu is open
        disableAutoFocus={false} // Auto focus on menu when opened
        disableRestoreFocus={false} // Restore focus after menu is closed
      >
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleSummarize}>
          <ListItemIcon>
            <AutoAwesome fontSize="small" sx={{ color: theme.palette.secondary.main }} />
          </ListItemIcon>
          <ListItemText>Summarize</ListItemText>
        </MenuItem>
        {onViewDetails && (
          <MenuItem onClick={handleViewDetails}>
            <ListItemIcon>
              <Visibility fontSize="small" sx={{ color: theme.palette.info.main }} />
            </ListItemIcon>
            <ListItemText>View Details</ListItemText>
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={handleDelete}>
          <ListItemIcon>
            <Delete fontSize="small" sx={{ color: theme.palette.error.main }} />
          </ListItemIcon>
          <ListItemText sx={{ color: theme.palette.error.main }}>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </motion.div>
  );
};

export default TaskCard;